/**
* This module is designed to seamlessly modify the map's view in response to location changes.
* Such changes can occur during the initial page load or when users utilize the typeahead feature to navigate to 
* new locations. The fundamental aim of this module is to elevate user experience by maintaining an ideal zoom level
* on the map. By doing so, users can effortlessly explore all pertinent results within their designated
* area of interest, eliminating the need for manual adjustments.
*/

import { updateBoundaries, drawBoundary } from 'libraries/mapLibrary';
import { store$ } from '@redux/store';
import { 
  distinctUntilChanged,
  filter,
  map,
  distinctUntilKeyChanged,
  withLatestFrom,
  tap,
  fromEvent,
  combineLatest } from 'rxjs';
import messageService from 'layout/services/messageService';
import { LocationType, selectLocation } from "features/searchResultsFacets";
import { isSearchWithinMapEnabled, isMapLibraryLoaded } from '../redux/selectors';
import { locationSearchParameters } from '../constants/searchParametersConstants';

const message$ = messageService.getMessage();
const boundaryProperties = ["MinLatitude", "MinLongitude", "MaxLatitude", "MaxLongitude"];

/**
 * This observable tells us whenever there was a custom event triggered in the document
 * indicading the presence of new boundaries shown in the map.
 */
const onNewBoundariesShown$ = fromEvent(document, 'newBoundariesShown');

/**
 * This observable tells us whenever there was a location change in the store.
 */
const newLocation$ = store$
.pipe(
  filter(selectLocation),
  filter((state) => !isSearchWithinMapEnabled(state)),
  filter((state) => locationSearchParameters(state).locationType !== LocationType.RectangularArea.toString()),
  map(selectLocation),
  distinctUntilChanged()
)

/**
 * This observable tells us whenever the map is ready to receive operations on it.
 */
const onMapLoaded$ = store$
.pipe(
  filter(isMapLibraryLoaded),
  map(isMapLibraryLoaded),
  distinctUntilChanged()
)

/**
 * Subscribing to the messaging service.
 * When there are new boundaries that contains a results set, map changes its geoCoordinates to these given boundaries.
 * As new boundaries constitute the core observable, the withLatestFrom operator pairs most recent boundaries fetched
 * with the latest location information, facilitated by the distinctUntilKeyChanged opertor.
 * Consequently, the resulting mapped value consistently represents both the boundaries and the user's current map view.
 * This observable will emit if and only if the map is ready, that is why we have a combineLatest operator on both
 * observables: on new messages, and on map loaded.
 */
combineLatest([message$, onMapLoaded$])
.pipe(
  map(([newMessages]) => newMessages),
  withLatestFrom(newLocation$),
  map(([newMessages, newLocationSource]) => ({
    locations: newLocationSource,
    boundaries: newMessages.payload.mapBoundaries,
    messageType: newMessages.messageType,
  })),
  distinctUntilKeyChanged('locations'),
  filter((information) => information.messageType === 'mapBoundaries'),
  tap((information) => { drawBoundary(information.boundaries?.BoundaryUrl) }),
  filter((information) => information.boundaries),
  filter((information) => !information.boundaries?.BoundaryUrl)
  ).subscribe((information) => setTimeout(updateBoundaries.bind(this, information.boundaries), 0));

/*
  Subscribing to the store.
  When search within map is enabled, we have to clean the search boundary since results are
  going to be shown outside of it.
  The way of cleaning it is to call the function that draws boundaries with a null argument for the boundary url.
*/
store$
  .pipe(
    map(isSearchWithinMapEnabled),
    distinctUntilChanged()
  )
  .subscribe((isSearchWithinMap) => {
    if (isSearchWithinMap) {
      drawBoundary();
    }
  });

  /**
   * Subscribing to the messaging service.
   * When there are new boundaries for the auto zoom fit functionality on paid markers, and also there is a
   * new boundary shown on the map that shows a visual hint for the location searched, this observable will
   * grab both pieces of information and auto fit the map zoom so the boundary and the paid markers are all
   * visible on screen.
   * This is particular useful for searches like zip codes, where the search type is not exact but radius, so
   * information appear outside of the boundary around the interested area.
   * This observable will emit if and only if the map is ready, that is why we have a combineLatest operator on both
   * observables: on new boundaries, and on map loaded.
   */
  combineLatest([onNewBoundariesShown$, onMapLoaded$])
  .pipe(
    map(([newBoundary]) => newBoundary),
    withLatestFrom(message$, newLocation$),
    map(([newBoundary, newMessage, newLocation]) => {
      /*
        if there is no marker boundaries, it means either we are showing just basic results, or it could be that
        there are no results at all to show based on the user's search criteria. By rule, we don't do auto fit on
        basic results. Additionally, in cases where there are no results, there are no subjects to auto-fit on the map.
        For these scenarios, we are relying just on the location boundaries (if any) for auto fit the map zoom,
        since this is, at the end of the day, the area the user is interested in finding a home.
      */
      const areThereMarkerBoundaries = newMessage?.payload?.mapBoundaries &&
        boundaryProperties.every((property) => Object.hasOwn(newMessage?.payload?.mapBoundaries, property));

      const markerBoundary = areThereMarkerBoundaries
        ? newMessage?.payload?.mapBoundaries
        : newBoundary?.detail?.boundaries;

      return {
        markerBoundary,
        locationBoundary: newBoundary?.detail?.boundaries,
        locationType: newLocation?.locationType?.toLowerCase(),
      }
    }),
    filter(({ locationBoundary }) =>
      boundaryProperties.every((property) => Object.hasOwn(locationBoundary, property))),
    map(({ locationBoundary, markerBoundary }) => ({
      MinLatitude: Math.min(locationBoundary.MinLatitude, markerBoundary.MinLatitude),
      MaxLatitude: Math.max(locationBoundary.MaxLatitude, markerBoundary.MaxLatitude),
      MinLongitude: Math.min(locationBoundary.MinLongitude, markerBoundary.MinLongitude),
      MaxLongitude: Math.max(locationBoundary.MaxLongitude, markerBoundary.MaxLongitude),
    })))
  .subscribe((boundaries) => setTimeout(updateBoundaries.bind(this, boundaries), 0));